import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { videoAPI, storyAPI } from '../../lib/api';

export default function SplashScreen({ onComplete }) {
  const [visible, setVisible] = useState(true);
  const [videoUrl, setVideoUrl] = useState(null);
  const [canSkip, setCanSkip] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    // Check if user already saw splash this session
    if (sessionStorage.getItem('mm_splash_seen')) {
      setVisible(false);
      onComplete();
      return;
    }

    // Fetch promo video
    const fetchVideo = async () => {
      try {
        const res = await storyAPI.getLatest();
        if (res.data?.video_id) {
          setVideoUrl(storyAPI.fileUrl(res.data.video_id));
          return;
        }
      } catch {}
      try {
        const res = await videoAPI.getLatest();
        if (res.data?.file_id) {
          setVideoUrl(videoAPI.fileUrl(res.data.file_id));
          return;
        }
      } catch {}
      // No video found — skip splash
      sessionStorage.setItem('mm_splash_seen', '1');
      setVisible(false);
      onComplete();
    };
    fetchVideo();

    // Allow skip after 2 seconds
    const timer = setTimeout(() => setCanSkip(true), 2000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleComplete = () => {
    sessionStorage.setItem('mm_splash_seen', '1');
    setVisible(false);
    onComplete();
  };

  const handleVideoEnd = () => {
    handleComplete();
  };

  if (!visible) return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.8 }}
          className="fixed inset-0 z-[9999] bg-black flex items-center justify-center"
          data-testid="splash-screen"
        >
          {videoUrl ? (
            <video
              ref={videoRef}
              src={videoUrl}
              autoPlay
              muted
              playsInline
              onEnded={handleVideoEnd}
              className="w-full h-full object-cover"
              data-testid="splash-video"
            />
          ) : (
            /* Fallback: animated M logo */
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 1, ease: 'easeOut' }}
              className="text-center"
            >
              <span className="font-display font-black text-[180px] text-[#b8ff00] leading-none">M</span>
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: '100%' }}
                transition={{ delay: 0.5, duration: 1.5 }}
                className="h-1 bg-[#b8ff00] mx-auto mt-4 rounded-full"
              />
            </motion.div>
          )}

          {/* Skip button */}
          {canSkip && (
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              onClick={handleComplete}
              className="absolute bottom-10 right-10 px-6 py-3 bg-white/10 backdrop-blur-sm border border-white/20 text-white text-sm font-medium rounded-full hover:bg-white/20 transition-all"
              data-testid="splash-skip-btn"
            >
              Skip
            </motion.button>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
